import { XIcon } from '@heroicons/react/outline';
import { Modal } from 'antd';
import { ZebraLearnLogo } from 'assets';
import Image from 'next/image';
import React from 'react';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { IntlTelInput } from '@/components/shared/IntlTelInput';

import { getGlobalCountry } from '@/atoms/countryState';

interface ModalComponentProps {
  visible: boolean;
  onCancel: () => void;
  isClosable?: boolean;
  errorsPassword: { password: string };
  errorsPhone: { phone: string };
  isGlobal?: boolean;
  setFormDataPassword: React.Dispatch<
    React.SetStateAction<{ password: string }>
  >;
  setFormDataPhone: React.Dispatch<
    React.SetStateAction<{
      phone: string;
      phoneCountryCode: string;
      phonePrefix: string;
    }>
  >;
  formDataPassword: { password: string };
  formDataPhone: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  };
  handlePhoneWithPassword: () => void;
  handlePhoneInput: (input: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => void;
  loading: boolean;
  userName?: string;
}

const PhonePasswordLiveModal: React.FC<ModalComponentProps> = ({
  visible,
  onCancel,
  isClosable = true,
  setFormDataPassword,
  handlePhoneWithPassword,
  loading,
  errorsPhone,
  formDataPhone,
  handlePhoneInput,
  setFormDataPhone,
  isGlobal = false,
  userName,
}) => {
  const handleClose = () => {
    onCancel();
    setFormDataPassword(() => ({
      password: '',
    }));
    setFormDataPhone({
      phone: '',
      phoneCountryCode: '',
      phonePrefix: '',
    });
  };
  const globalCountry = getGlobalCountry();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handlePhoneWithPassword();
  };
  return (
    <Modal
      title=''
      open={visible}
      centered={true}
      closable={false}
      onCancel={handleClose}
      footer={false}
      destroyOnHidden
      width={500}
      maskClosable={isClosable}
      className='customLoginModal bg-bgColor bg-opacity-70 pt-5 px-10 rounded-2xl border-2 border-borderDarkGray backdrop-filter backdrop-blur-md'
    >
      <div className='flex items-center gap-y-10 flex-col justify-center'>
        <div className='z-30 flex flex-col gap-y-1 w-full'>
          <ShouldRender check={!isGlobal}>
            <div className='relative pt-4'>
              <span className='flex justify-center w-full'>
                <Image
                  src={ZebraLearnLogo}
                  alt='Logo'
                  priority
                  className='object-contain h-3.5 w-full'
                />
              </span>
              <ShouldRender check={isClosable}>
                <span
                  onClick={handleClose}
                  className='absolute right-0 top-[40%] cursor-pointer'
                >
                  <XIcon className='w-6 h-6 text-fadedWhite' />
                </span>
              </ShouldRender>
            </div>
            <div className='border-b-2 border-backgroundGray py-3' />
          </ShouldRender>

          <div className='flex items-center gap-y-10 flex-col justify-center'>
            <div className='z-30 py-6 flex flex-col w-full gap-y-1'>
              <ShouldRender check={!isGlobal}>
                <h1 className='text-xl text-fadedWhite font-medium mt-1 leading-10 text-center w-full'>
                  Enter your{' '}
                  <b className='text-primary font-medium'>WhatsApp Number</b>
                </h1>
                <span className='text-textGray text-center text-base'>
                  We’ll share the workshop link with you on WhatsApp before it
                  starts.
                </span>
              </ShouldRender>
              <ShouldRender check={isGlobal}>
                <h1 className='text-xl text-fadedWhite font-medium mt-1leading-10 w-full'>
                  Hi {userName} ,
                </h1>
                <span className='text-textGray text-start text-base'>
                  You are one step away from logging into your account.
                </span>
              </ShouldRender>
              <form
                onSubmit={handleSubmit}
                className='flex flex-col gap-2 mt-4 w-full'
              >
                <div className='col-span-1 flex-col flex gap-y-.5'>
                  <label className='text-sm font-medium md:text-base text-textGrayProfile pb-2'>
                    Mobile Number
                  </label>
                  <div className='relative'>
                    <IntlTelInput
                      phone={formDataPhone.phone}
                      phoneCountryCode={
                        formDataPhone.phoneCountryCode ||
                        globalCountry?.countryCode?.toLowerCase() ||
                        'in'
                      }
                      onChange={({ phone, phoneCountryCode, phonePrefix }) => {
                        const cleanedPhone = phone.replace(/^0+/, '');
                        setFormDataPhone({
                          phone: cleanedPhone,
                          phoneCountryCode,
                          phonePrefix,
                        });

                        handlePhoneInput({
                          phone: cleanedPhone,
                          phoneCountryCode,
                          phonePrefix,
                        });
                      }}
                    />
                  </div>
                  {errorsPhone.phone && (
                    <p className='text-red-500'>{errorsPhone.phone}</p>
                  )}
                </div>

                <span className='flex justify-center mt-2'>
                  <ButtonDefault
                    size={4}
                    variant={
                      isGlobal ? ButtonVariants.PRIMARY : ButtonVariants.WHITE
                    }
                    className='p-1 w-full'
                    enableScaling={false}
                    rounded={false}
                    roundedSm
                    loading={loading}
                    onClick={handleSubmit}
                  >
                    <span className='flex text-sm font-bold gap-x-2 px-4 py-1 whitespace-nowrap'>
                      Update
                    </span>
                  </ButtonDefault>
                </span>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PhonePasswordLiveModal;
